/**
 * 🎯 FörderMatchCard Component
 * ===========================
 * 
 * Intelligente Förder-Empfehlungscard basierend auf 95% ML-Konfidenz Durum-Preisprognosen.
 * 
 * Features:
 * - Zeigt warum das Programm zum aktuellen Markttrend passt
 * - Ampel-System für Aufwand-Assessment
 * - Konfidenz-basierte Handlungsempfehlungen
 * - Mobile-responsive Design mit Swipe-Funktionalität
 * - Click-Tracking für Analytics
 * 
 * @param {Object} program - Matched funding program data
 * @param {Function} onDetailsClick - Handler for "Details ansehen" 
 * @param {Function} onTrackInteraction - Analytics tracking
 * 
 * @author CENTEO Development Team
 * @date 2025-08-09
 */

import React, { useState } from 'react';

const FörderMatchCard = ({ 
  program, 
  onDetailsClick, 
  onTrackInteraction,
  compact = false,
  className = ''
}) => {
  // No translations needed here; static labels are multilingual or icons
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!program) return null;

  const handleDetailsClick = () => {
    onTrackInteraction?.(program.id, 'details_clicked');
    onDetailsClick?.(program);
  };

  const handleCardClick = () => {
    if (!isExpanded) {
      onTrackInteraction?.(program.id, 'card_expanded');
      setIsExpanded(true);
    }
  };

  const formatFunding = (fundingData) => {
    if (fundingData?.formatted && fundingData.formatted !== '0 €') {
      return fundingData.formatted;
    }
    if (fundingData?.raw) {
      // Extrahiere erste Zahl aus raw text
      const match = fundingData.raw.match(/([0-9.,]+)\\s*([€$]|EUR|€\/|Mio|Tsd)/);
      if (match) {
        return match[0];
      }
      return fundingData.raw.substring(0, 30) + '...';
    }
    return 'Auf Anfrage';
  };

  const getUrgencyBadge = (urgency) => {
    const urgencyConfig = {
      high: { 
        color: 'bg-red-500', 
        text: '🚨 Dringend',
        textColor: 'text-white'
      },
      medium: { 
        color: 'bg-yellow-500', 
        text: '⚡ Zeitnah',
        textColor: 'text-white'
      },
      low: { 
        color: 'bg-green-500', 
        text: '✅ Geplant',
        textColor: 'text-white'
      }
    };

    const config = urgencyConfig[urgency] || urgencyConfig.low;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color} ${config.textColor}`}>
        {config.text}
      </span>
    );
  };

  const getCountryFlag = (countryCode) => {
    const flags = {
      'DE': '🇩🇪',
      'IT': '🇮🇹', 
      'ES': '🇪🇸',
      'FR': '🇫🇷',
      'EU': '🇪🇺'
    };
    return flags[countryCode] || '🌍';
  };

  return (
    <div 
      className={`
        bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden
        transition-all duration-300 hover:shadow-xl hover:border-blue-200
        cursor-pointer
        ${compact ? 'p-4' : 'p-6'}
        ${className}
      `}
      onClick={handleCardClick}
    >
      {/* Header mit Rang und Land */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full text-sm font-bold">
            #{program.rank}
          </div>
          <div className="text-2xl">
            {getCountryFlag(program.country)}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {getUrgencyBadge(program.urgency)}
          <div className="text-2xl">{program.trafficLight.color}</div>
        </div>
      </div>

      {/* Programm-Titel */}
      <h3 className={`font-bold text-gray-800 mb-3 ${compact ? 'text-lg' : 'text-xl'} leading-tight`}>
        {program.title}
      </h3>

      {/* Förder-Info */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <div className="text-sm text-gray-600 mb-1">Förderung</div>
          <div className="text-lg font-semibold text-green-600">
            {formatFunding(program.fundingAmount)}
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-sm text-gray-600 mb-1">Aufwand</div>
          <div className="flex items-center gap-1">
            <span className="text-lg">{program.trafficLight.color}</span>
            <span className="text-sm font-medium">{program.trafficLight.label}</span>
          </div>
        </div>
      </div>

      {/* Relevanz-Erklärung (Highlighted) */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4 rounded-r-lg">
        <div className="text-sm font-semibold text-blue-800 mb-1">
          🎯 Warum jetzt relevant:
        </div>
        <div className="text-sm text-blue-700 leading-relaxed">
          {program.whyRelevant}
        </div>
      </div>

      {/* Handlungsempfehlung */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg mb-4">
        <div className="text-sm font-semibold text-gray-800 mb-1">
          💡 Empfehlung:
        </div>
        <div className="text-sm text-gray-700">
          {program.actionAdvice}
        </div>
      </div>

      {/* Expandierte Details */}
      {isExpanded && !compact && (
        <div className="border-t pt-4 space-y-3 animate-fadeIn">
          {/* Träger & Timeline */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-600">Träger:</span>
              <div className="text-gray-800">{program.details.carrier}</div>
            </div>
            <div>
              <span className="font-medium text-gray-600">Timeline:</span>
              <div className="text-gray-800">{program.details.timeline}</div>
            </div>
          </div>

          {/* Zielgruppe */}
          {program.details.target?.length > 0 && (
            <div className="text-sm">
              <span className="font-medium text-gray-600">Zielgruppe:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {program.details.target.map((target, index) => (
                  <span 
                    key={index}
                    className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs"
                  >
                    {target}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Kombinierbarkeit */}
          {program.details.combinable && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-green-600">✅</span>
              <span className="text-gray-700">Mit anderen Programmen kombinierbar</span>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 mt-4">
        <button
          onClick={handleDetailsClick}
          className="
            flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg 
            text-sm font-medium transition-colors duration-200
            flex items-center justify-center gap-2
          "
        >
          <span>📋</span>
          Details ansehen
        </button>
        
        {!isExpanded && !compact && (
          <button
            onClick={handleCardClick}
            className="
              px-4 py-2 border border-gray-300 hover:border-gray-400 
              text-gray-700 rounded-lg text-sm font-medium 
              transition-colors duration-200
            "
          >
            Mehr Info
          </button>
        )}
      </div>

      {/* Match Score für Development (nur in Dev-Mode) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-3 text-xs text-gray-400 text-center">
          Match Score: {program.matchScore} | ID: {program.id}
        </div>
      )}
    </div>
  );
};

/**
 * Swipeable Container für Mobile-Optimierung
 */
export const SwipeableFörderCards = ({ programs, onDetailsClick, onTrackInteraction }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () => {
    setCurrentIndex(prev => prev > 0 ? prev - 1 : programs.length - 1);
  };

  const goToNext = () => {
    setCurrentIndex(prev => prev < programs.length - 1 ? prev + 1 : 0);
  };

  if (!programs?.length) return null;

  return (
    <div className="relative overflow-hidden">
      {/* Mobile Swipe Container */}
      <div 
        className="flex transition-transform duration-300 ease-in-out md:hidden"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {programs.map((program, index) => (
          <div key={program.id} className="w-full flex-shrink-0 px-2">
            <FörderMatchCard 
              program={program}
              onDetailsClick={onDetailsClick}
              onTrackInteraction={onTrackInteraction}
              compact={true}
            />
          </div>
        ))}
      </div>

      {/* Desktop Grid */}
      <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {programs.map((program) => (
          <FörderMatchCard 
            key={program.id}
            program={program}
            onDetailsClick={onDetailsClick}
            onTrackInteraction={onTrackInteraction}
          />
        ))}
      </div>

      {/* Mobile Navigation */}
      {programs.length > 1 && (
        <div className="flex justify-center items-center mt-4 gap-4 md:hidden">
          <button 
            onClick={goToPrevious}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
          >
            ←
          </button>
          
          <div className="flex gap-2">
            {programs.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentIndex ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
          
          <button 
            onClick={goToNext}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
          >
            →
          </button>
        </div>
      )}

      {/* Counter */}
      <div className="text-center mt-3 text-sm text-gray-600 md:hidden">
        {currentIndex + 1} von {programs.length} Empfehlungen
      </div>
    </div>
  );
};

// Confidence Badge Component
export const ConfidenceBadge = ({ confidence, scenario }) => {
  const getConfidenceConfig = (level) => {
    if (level >= 95) {
      return {
        color: 'bg-green-600',
        text: '🔥 SEHR SICHER',
        description: 'Handeln empfohlen'
      };
    } else if (level >= 90) {
      return {
        color: 'bg-blue-600', 
        text: '✅ HOHE SICHERHEIT',
        description: 'Gute Basis für Entscheidung'
      };
    } else if (level >= 80) {
      return {
        color: 'bg-yellow-600',
        text: '⚠️ SOLIDE PROGNOSE', 
        description: 'Mit Vorsicht planen'
      };
    } else {
      return {
        color: 'bg-gray-600',
        text: '🤔 UNSICHER',
        description: 'Weitere Daten abwarten'
      };
    }
  };

  const config = getConfidenceConfig(confidence);

  return (
    <div className="bg-white border-2 border-blue-200 rounded-xl p-4 mb-6">
      <div className="flex items-center justify-between mb-2">
        <span className={`px-3 py-1 rounded-full text-white text-sm font-bold ${config.color}`}>
          {config.text} ({confidence}%)
        </span>
        <span className="text-sm text-gray-600 font-medium">
          Basiert auf 1396 Marktdaten
        </span>
      </div>
      
      <div className="text-sm text-gray-700">
        <span className="font-semibold">Marktlage:</span> {scenario}
      </div>
      <div className="text-xs text-gray-600 mt-1">
        {config.description}
      </div>
    </div>
  );
};

export default FörderMatchCard;
